import { seatPan } from '@chessmate/audio';
import { playSfx } from '../services/audio.js';

/**
 * Shared sound cues for the table games (uplift slice U6, docs/audits/2026-11-ui-uplift-sound.md).
 * Every store names the same events the same way, so a silent game is one missing call here, not a
 * different idea of what should make a noise. These sit on top of `playSfx`, which already honours
 * mute, volumes, the sensory preset and the parent locks.
 */
export type RoundOutcome = 'won' | 'lost' | 'draw';

/** A short stinger at the summary of a round / hand / deal (the game-ending `win`/`lose` replaces it). */
export function cueRound(outcome: RoundOutcome): void {
  playSfx(outcome === 'won' ? 'round-won' : outcome === 'lost' ? 'round-lost' : 'round-draw');
}

/** The end of a whole game: `win` or `lose`, or `draw` for a level finish. */
export function cueGame(outcome: RoundOutcome): void {
  playSfx(outcome === 'won' ? 'win' : outcome === 'lost' ? 'lose' : 'draw');
}

/** Either the round stinger, or the game one when this round also ended the game. */
export function cueRoundOrGame(outcome: RoundOutcome, gameOver: boolean): void {
  if (gameOver) cueGame(outcome);
  else cueRound(outcome);
}

/** A card reaching the table, heard from the seat it came from. */
export function cueCardPlay(seat: string | null | undefined): void {
  playSfx('card-play', seatPan(seat));
}

/** The cards going out. */
export function cueDeal(): void {
  playSfx('card-deal');
}

/** A trick that was just completed and swept: the trick on the table emptied while the count of won tricks grew. */
export function cueSweep(before: { readonly tricks: { readonly length: number } } | null | undefined, after: { readonly trick: { readonly length: number }; readonly tricks: { readonly length: number } }): void {
  if (before && after.trick.length === 0 && after.tricks.length > before.tricks.length) playSfx('trick-sweep');
}

/**
 * A soft "your move" cue, once per turn: only when the actor changes TO the human, never on a render
 * and never when the human's own move leaves the move with them again.
 */
export function cueTurn(previous: string | null | undefined, next: string | null | undefined, human = 'S'): void {
  if (next === human && previous !== human) playSfx('turn-yours');
}
